const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function setActive(el, on) {
  el.classList.toggle('is-on', on);
  if (on) el.removeAttribute('aria-hidden');
  else el.setAttribute('aria-hidden', 'true');
}

function initRail(root) {
  const rail = root.querySelector('[data-rh-rail]');
  const arrows = [...root.querySelectorAll('[data-rh-step]')];
  if (!rail) return;
  const step = (dir) => {
    const card = rail.querySelector('[data-rh-card]');
    if (!card) return;
    const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
    const perView = Math.max(1, Math.floor(rail.clientWidth / (card.offsetWidth + gap)));
    rail.scrollBy({ left: dir * perView * (card.offsetWidth + gap), behavior: reduceMotion ? 'auto' : 'smooth' });
  };
  const sync = () => {
    const [prev, next] = arrows;
    if (prev) prev.disabled = rail.scrollLeft < 4;
    if (next) next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
  };
  arrows.forEach((btn) => btn.addEventListener('click', () => step(Number(btn.dataset.rhStep))));
  rail.addEventListener('scroll', sync, { passive: true });
  window.addEventListener('resize', sync, { passive: true });
  sync();
}

function initTopics(root) {
  const chips = [...root.querySelectorAll('[data-rh-topic]')];
  const cards = [...root.querySelectorAll('[data-rh-card]')];
  const rows = [...root.querySelectorAll('[data-rv-row]')];
  const rest = root.querySelector('[data-rv-rest]');
  const rail = root.querySelector('[data-rh-rail]');
  const misses = (el, topic) => topic !== 'all' && !el.dataset.topics.split(' ').includes(topic);
  chips.forEach((chip) => chip.addEventListener('click', () => {
    const topic = chip.dataset.rhTopic;
    chips.forEach((c) => {
      const on = c === chip;
      c.classList.toggle('active', on);
      c.setAttribute('aria-pressed', String(on));
    });
    cards.forEach((card) => { card.hidden = misses(card, topic); });
    rows.forEach((row) => { row.hidden = misses(row, topic); });
    if (rest && topic !== 'all') rest.open = true;
    if (rail) {
      rail.scrollLeft = 0;
      rail.dispatchEvent(new Event('scroll'));
    }
  }));
}

function initLightbox(root) {
  const box = root.querySelector('[data-rh-box]');
  const content = root.querySelector('[data-rh-box-content]');
  const allCards = [...root.querySelectorAll('[data-rh-card]')];
  if (!box || !content || !allCards.length || typeof box.showModal !== 'function') return;
  let cards = allCards;
  let index = 0;
  let opener = null;

  const render = (i) => {
    index = (i + cards.length) % cards.length;
    const tpl = cards[index].querySelector('[data-rh-full]');
    content.replaceChildren(tpl.content.cloneNode(true));
    content.scrollTop = 0;
    box.querySelectorAll('[data-rh-box-step]').forEach((btn) => { btn.hidden = cards.length < 2; });
    box.dispatchEvent(new CustomEvent('rh:view', { bubbles: true, detail: cards[index] }));
  };
  const open = (card, from) => {
    cards = card.hidden ? allCards : allCards.filter((c) => !c.hidden);
    opener = from || null;
    render(cards.indexOf(card));
    box.showModal();
    document.documentElement.classList.add('rh-locked');
  };

  allCards.forEach((card) => card.querySelector('[data-rh-open]').addEventListener('click', (e) => open(card, e.currentTarget)));
  root.querySelectorAll('[data-rh-face]').forEach((face) => face.addEventListener('click', () => {
    const card = allCards.find((c) => c.dataset.rhKey === face.dataset.rhFace) || allCards[0];
    open(card, face);
  }));
  box.querySelectorAll('[data-rh-box-step]').forEach((btn) => btn.addEventListener('click', () => render(index + Number(btn.dataset.rhBoxStep))));
  box.querySelector('[data-rh-close]').addEventListener('click', () => box.close());
  box.addEventListener('click', (e) => { if (e.target === box) box.close(); });
  box.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') render(index + 1);
    if (e.key === 'ArrowLeft') render(index - 1);
  });
  let touchX = null;
  box.addEventListener('touchstart', (e) => { touchX = e.touches[0].clientX; }, { passive: true });
  box.addEventListener('touchend', (e) => {
    if (touchX === null) return;
    const dx = e.changedTouches[0].clientX - touchX;
    touchX = null;
    if (Math.abs(dx) > 60 && !e.target.closest('.rh-box-media')) render(index + (dx < 0 ? 1 : -1));
  }, { passive: true });
  box.addEventListener('close', () => {
    document.documentElement.classList.remove('rh-locked');
    content.replaceChildren();
    const openerShown = opener?.isConnected && opener.checkVisibility?.({ visibilityProperty: true });
    (openerShown ? opener : cards[index]?.querySelector('[data-rh-open]'))?.focus();
  });
}

function initMarquee(root) {
  const track = root.querySelector('.tvh-marquee-track');
  if (!track || reduceMotion) return;
  [...track.children].forEach((tile) => {
    const copy = tile.cloneNode(true);
    copy.setAttribute('aria-hidden', 'true');
    copy.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
    track.append(copy);
  });
  track.style.setProperty('--tvh-marquee-s', `${Math.max(40, track.children.length * 3)}s`);
  root.classList.add('is-looping');
}

function initRealHomes(root) {
  initTopics(root);
  initRail(root);
  initLightbox(root);
}

function loadThumb(item) {
  const img = item.querySelector('img[data-src]');
  if (!img) return;
  img.src = img.dataset.src;
  img.removeAttribute('data-src');
}

function initRotator(root) {
  initLightbox(root);
  const items = [...root.querySelectorAll('[data-pq-item]')];
  const dots = [...root.querySelectorAll('[data-pq-dot]')];
  const toggle = root.querySelector('[data-pq-toggle]');
  const stage = root.querySelector('.pq-items');
  if (items.length < 2) return;
  root.style.setProperty('--pq-dur', `${Math.max(4, Number(root.dataset.pqInterval) || 6)}s`);
  let index = 0;
  const holds = new Set(reduceMotion ? ['motion'] : []);

  const sync = () => {
    if (holds.size) root.dataset.paused = '';
    else delete root.dataset.paused;
    if (!toggle) return;
    const off = holds.has('user');
    toggle.setAttribute('aria-pressed', String(off));
    toggle.setAttribute('aria-label', off ? 'Play reviews' : 'Pause reviews');
  };
  const hold = (reason, on) => {
    if (on) holds.add(reason);
    else holds.delete(reason);
    sync();
  };

  const show = (i) => {
    index = (i + items.length) % items.length;
    items.forEach((item, n) => setActive(item, n === index));
    dots.forEach((dot, n) => {
      dot.classList.remove('is-on');
      dot.classList.toggle('is-done', n < index);
      if (n === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    void root.offsetWidth;
    dots[index]?.classList.add('is-on');
    loadThumb(items[index]);
    loadThumb(items[(index + 1) % items.length]);
  };

  root.addEventListener('animationend', (e) => {
    if (e.animationName === 'pq-fill' && e.target.closest('[data-pq-dot]') === dots[index]) show(index + 1);
  });
  dots.forEach((dot, n) => dot.addEventListener('click', () => show(n)));
  toggle?.addEventListener('click', () => hold('user', !holds.has('user')));
  root.querySelectorAll('[data-rh-open]').forEach((btn) => btn.addEventListener('click', () => hold('user', true)));
  root.addEventListener('rh:view', (e) => {
    const i = items.indexOf(e.detail);
    if (i >= 0) show(i);
  });

  let startX = null;
  let swiped = false;
  stage.addEventListener('pointerdown', (e) => {
    startX = e.clientX;
    swiped = false;
  });
  stage.addEventListener('pointercancel', () => { startX = null; });
  stage.addEventListener('pointerup', (e) => {
    if (startX === null) return;
    const dx = e.clientX - startX;
    startX = null;
    if (Math.abs(dx) < 40) return;
    swiped = true;
    show(index + (dx < 0 ? 1 : -1));
  });
  stage.addEventListener('click', (e) => {
    if (!swiped) return;
    swiped = false;
    e.preventDefault();
    e.stopPropagation();
  }, true);

  root.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hold('hover', true); });
  root.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') hold('hover', false); });
  root.addEventListener('focusin', (e) => { if (e.target.matches(':focus-visible')) hold('focus', true); });
  root.addEventListener('focusout', (e) => { if (!root.contains(e.relatedTarget)) hold('focus', false); });
  const onVisibility = () => {
    if (!root.isConnected) {
      document.removeEventListener('visibilitychange', onVisibility);
      return;
    }
    hold('hidden', document.hidden);
  };
  document.addEventListener('visibilitychange', onVisibility);
  sync();
  show(0);
}

function relativeTime(iso) {
  const hours = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 3600000));
  if (hours < 24) return hours === 1 ? 'about an hour ago' : `${hours} hours ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

function readSession(key) {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function writeSession(key, value) {
  try { sessionStorage.setItem(key, value); } catch {}
}

function initToast(toast) {
  const dataEl = document.querySelector('[data-tv-toast-data]');
  if (!dataEl || readSession('tvToastOff') === '1') return;
  if (!window.matchMedia('(min-width: 750px)').matches) return;
  let orders = [];
  try { orders = JSON.parse(dataEl.textContent) || []; } catch { return; }
  const maxDays = Number(toast.dataset.tvToastDays) || 7;
  const cutoff = Date.now() - maxDays * 86400000;
  orders = orders.filter((o) => o && o.at && new Date(o.at).getTime() >= cutoff && o.city && o.product);
  if (!orders.length) return;

  const maxShows = Number(toast.dataset.tvToastMax) || 3;
  let shown = Number(readSession('tvToastShown') || 0);
  let next = Number(readSession('tvToastNext') || 0);
  const link = toast.querySelector('[data-tv-toast-link]');
  const img = toast.querySelector('[data-tv-toast-img]');
  const line = toast.querySelector('[data-tv-toast-line]');
  const when = toast.querySelector('[data-tv-toast-when]');
  let hideTimer = null;

  const hide = () => { toast.classList.remove('is-in'); setTimeout(() => { toast.hidden = true; }, reduceMotion ? 0 : 260); };

  const showOne = () => {
    if (shown >= maxShows) return;
    if (document.hidden) {
      setTimeout(showOne, 32000);
      return;
    }
    const order = orders[next % orders.length];
    next += 1;
    shown += 1;
    writeSession('tvToastShown', String(shown));
    writeSession('tvToastNext', String(next));
    const where = order.region ? `${order.city}, ${order.region}` : order.city;
    const what = order.qty > 1 ? `${order.qty} × ${order.product}` : `the ${order.product}`;
    line.textContent = `Someone in ${where} ordered ${what}`;
    when.textContent = relativeTime(order.at);
    link.href = order.handle ? `/products/${order.handle}` : '#';
    if (order.image) { img.src = order.image; img.hidden = false; } else { img.hidden = true; }
    toast.hidden = false;
    requestAnimationFrame(() => toast.classList.add('is-in'));
    clearTimeout(hideTimer);
    hideTimer = setTimeout(hide, 6500);
    if (shown < maxShows) setTimeout(showOne, 32000);
  };

  toast.querySelector('[data-tv-toast-close]').addEventListener('click', () => {
    writeSession('tvToastOff', '1');
    clearTimeout(hideTimer);
    hide();
    shown = maxShows;
  });

  setTimeout(showOne, 14000);
}

function initOnce(root, init) {
  if (root.dataset.proofReady) return;
  root.dataset.proofReady = '1';
  init(root);
}

function initWithin(scope) {
  scope.querySelectorAll('[data-rh]').forEach((root) => initOnce(root, initRealHomes));
  scope.querySelectorAll('[data-tvh-marquee]').forEach((root) => initOnce(root, initMarquee));
  scope.querySelectorAll('[data-pq]').forEach((root) => initOnce(root, initRotator));
}

initWithin(document);
document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
const toast = document.querySelector('[data-tv-toast]');
if (toast) initOnce(toast, initToast);
